/**
 * The step-up round trip shared by `<pk-step-up-gate>` and the key components.
 *
 * A gated action is a form-encoded POST to the host (the Umbraco adapter's key routes) with
 * `Accept: application/json`. The host answers:
 *   - 2xx `{ ok: true, redirect? }` — done;
 *   - 403 `{ error: "step-up-required", setupNeeded }` — the API wants a fresh TOTP (contract `x-step-up`);
 *   - 4xx/5xx `{ error, message }` — refused.
 * On a step-up the gate collects a code through `<pk-totp-reprompt>` (its `verify-url` exchanges the code for a
 * short-lived proof token) and re-dispatches the same action with the token in the `stepUpToken` field.
 *
 * Components inside a gate ask for it with `requestThroughGate()`: a bubbling, composed, cancelable
 * `pk-step-up-request` event. The gate cancels it (claims it) and settles the returned promise; when no gate
 * claims it the function returns `null` and the component falls back to a plain form POST.
 */
import { withCsrf } from './csrf.js';

/** The event a component dispatches to hand an action to the enclosing `<pk-step-up-gate>`. */
export const STEP_UP_REQUEST_EVENT = 'pk-step-up-request';

/** The default form field that carries the step-up proof on the retry. */
export const STEP_UP_TOKEN_FIELD = 'stepUpToken';

/** The API's step-up slug. */
export const STEP_UP_REQUIRED = 'step-up-required';

/** A gated action. */
export interface StepUpAction {
    /** POST URL. */
    url: string;
    /** HTTP method (default `POST`). */
    method?: string;
    /** Form fields (form-encoded). */
    fields?: Record<string, string>;
    /** What the TOTP prompt says is being confirmed. */
    label?: string;
}

/** How a gated action ended. */
export interface StepUpOutcome {
    /** True for a 2xx answer. */
    ok: boolean;
    /** HTTP status (0 = network failure or not sent). */
    status: number;
    /** The JSON body (null when there was none). */
    data: Record<string, unknown> | null;
    /** True when the user backed out of the TOTP prompt (or has no TOTP to step up with). */
    cancelled: boolean;
    /** The error slug of a failed answer. */
    error?: string;
}

/** Detail of `pk-step-up-request`: the action plus the callback the gate settles. */
export interface StepUpRequestDetail extends StepUpAction {
    settle: (outcome: StepUpOutcome) => void;
}

/** One raw answer. */
export interface ActionResponse {
    status: number;
    data: Record<string, unknown> | null;
}

/**
 * Hands `action` to the enclosing `<pk-step-up-gate>`. Returns the outcome promise when a gate claimed it,
 * `null` when none did (the caller then posts the form itself).
 */
export function requestThroughGate(from: Element, action: StepUpAction): Promise<StepUpOutcome> | null {
    let settle: (o: StepUpOutcome) => void = () => undefined;
    const promise = new Promise<StepUpOutcome>((resolve) => { settle = resolve; });
    const event = new CustomEvent<StepUpRequestDetail>(STEP_UP_REQUEST_EVENT, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: { ...action, settle },
    });
    const claimed = !from.dispatchEvent(event);
    return claimed ? promise : null;
}

/** The fields of a form as a plain object (last value wins; files are skipped). */
export function formFields(form: HTMLFormElement): Record<string, string> {
    const fields: Record<string, string> = {};
    new FormData(form).forEach((value, name) => {
        if (typeof value === 'string') fields[name] = value;
    });
    return fields;
}

/** POSTs an action (form-encoded, CSRF header, `Accept: application/json`), adding the step-up token when given. */
export async function sendAction(action: StepUpAction, stepUpToken: string | null, tokenField = STEP_UP_TOKEN_FIELD): Promise<ActionResponse> {
    const body = new URLSearchParams();
    for (const [name, value] of Object.entries(action.fields ?? {})) body.set(name, value);
    if (stepUpToken) body.set(tokenField, stepUpToken);
    try {
        const response = await fetch(action.url, withCsrf({
            method: (action.method ?? 'POST').toUpperCase(),
            headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        }));
        let data: Record<string, unknown> | null = null;
        try {
            const parsed: unknown = await response.json();
            data = parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null;
        } catch {
            data = null;
        }
        return { status: response.status, data };
    } catch {
        return { status: 0, data: null };
    }
}

/** Whether an answer is the API's step-up demand, and whether TOTP must be set up first. */
export function stepUpDemand(response: ActionResponse): { required: boolean; setupNeeded: boolean } {
    const required = response.status === 403 && response.data?.['error'] === STEP_UP_REQUIRED;
    return { required, setupNeeded: required && response.data?.['setupNeeded'] === true };
}
